import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ShieldIcon from "@mui/icons-material/Shield";
import FlightIcon from "@mui/icons-material/Flight";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EventIcon from "@mui/icons-material/Event";
import BespokeEventDialog from "../shared utils/BespokeEventDialog.jsx";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Tooltip from "@mui/material/Tooltip";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import { formatLocalDateOnly } from "../shared utils/dateFormatters";
import {
  getGroupPayments,
  createGroupPayment,
  updateGroupPayment,
  deleteGroupPayment,
  getGroupInvoice,
} from "./api/finance";
import { getBookingMeta, breakOutToIndividual, enableGroupBilling } from "./api/groups";
import GroupPaymentDialog from "./GroupPaymentDialog";
import { fetchJson } from "./utils/http";
import { SERVER_URL } from "../../constants";

const DISCOUNT_PREFIX = "DISCOUNT:";

const isDiscountPayment = (p) =>
  String(p?.paymentAccount || "").toUpperCase().startsWith(DISCOUNT_PREFIX);

const parseDiscountReason = (paymentAccount) => {
  const s = String(paymentAccount || "").trim();
  if (!s.toUpperCase().startsWith(DISCOUNT_PREFIX)) return "";
  return s.slice(DISCOUNT_PREFIX.length).trim();
};

const currency = (n) =>
  `£ ${Number(n || 0).toFixed(2)} GBP`;

function computeMemberFactor(member) {
  const agents = Array.isArray(member?.agents) ? member.agents : [];
  const totalCommission = agents.reduce(
    (acc, a) => acc + (Number(a.commissionRate) || 0),
    0
  );
  return (100 - totalCommission) / 100;
}

/**
 * Roll up every booking across all group members into line items keyed by
 * product name + dates + unit rate. Each line records the quantity and the
 * gross/net totals (net applies each member's commission factor per booking).
 */
function buildInvoiceLines(members) {
  const lines = new Map();

  (members || []).forEach((member) => {
    const factor = computeMemberFactor(member);
    const bookings = Array.isArray(member?.bookings) ? member.bookings : [];

    bookings.forEach((b) => {
      const charge = Number(b.actualCharge) || 0;
      const key = [
        String(b.productName || ""),
        String(b.startDate || ""),
        String(b.endDate || ""),
        charge.toFixed(2),
      ].join("||");

      if (!lines.has(key)) {
        lines.set(key, {
          productName: b.productName || "",
          startDate: b.startDate || "",
          endDate: b.endDate || "",
          rate: charge,
          qty: 0,
          grossTotal: 0,
          netTotal: 0,
          bookingIds: [],
        });
      }

      const line = lines.get(key);
      line.qty += 1;
      line.grossTotal += charge;
      line.netTotal += b.commissionable ? charge * factor : charge;
      const bid = b.bookingId || b.id;
      if (bid) line.bookingIds.push(bid);
    });
  });

  return Array.from(lines.values()).sort((a, b) => {
    const da = new Date(a.startDate || 0).getTime();
    const db = new Date(b.startDate || 0).getTime();
    if (da !== db) return da - db;
    return String(a.productName).localeCompare(String(b.productName));
  });
}

export default function GroupFinanceTab({ group, token, showToast, studentIds = [], adultIds = [] }) {
  const [invoice, setInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [gross, setGross] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [saving, setSaving] = useState(false);

  // Tracks meta.groupBilled — when false the group has been broken out for individual
  // billing and we render an explanatory blocker. null = meta still loading.
  const [groupBilled, setGroupBilled] = useState(null);
  // Tracks whether any individual student/adult payments have been recorded since the
  // group was broken out. Reading from meta.hasIndividualPayments (backend-derived).
  // When true, re-enabling group billing is locked because you'd be re-merging
  // individual payment records into a group-billed flow.
  const [hasIndividualPayments, setHasIndividualPayments] = useState(false);
  const [metaLoading, setMetaLoading] = useState(true);
  const [breakingOut, setBreakingOut] = useState(false);
  const [enablingBilling, setEnablingBilling] = useState(false);

  const reloadMeta = useCallback(async () => {
    if (!group?.id) return;
    setMetaLoading(true);
    try {
      const meta = await getBookingMeta(group.id, token);
      // Existing groups backfill to groupBilled=true server-side; default to true if missing.
      setGroupBilled(meta?.groupBilled !== false);
      // hasIndividualPayments is server-derived (backend checks payment tables for the
      // group's members). Defensive default: false when missing means re-enable stays
      // available — backend can opt in by populating the field.
      setHasIndividualPayments(meta?.hasIndividualPayments === true);
    } catch {
      // No meta at all (404). Treat as not-group-billed so we don't try to load /invoice.
      setGroupBilled(false);
      setHasIndividualPayments(false);
    } finally {
      setMetaLoading(false);
    }
  }, [group?.id, token]);

  useEffect(() => {
    let cancelled = false;
    reloadMeta();
    return () => { cancelled = true; void cancelled; };
  }, [reloadMeta]);

  const loadInvoice = useCallback(async () => {
    if (!group?.id) return;
    setLoadingInvoice(true);
    try {
      const data = await getGroupInvoice(group.id, token);
      setInvoice(data);
    } catch (err) {
      console.error("Failed to load group invoice", err);
      showToast?.("Failed to load group finance data", "error");
      setInvoice(null);
    } finally {
      setLoadingInvoice(false);
    }
  }, [group?.id, token, showToast]);

  useEffect(() => {
    // Wait until we know this group is still being billed as a group before loading
    // the group invoice — the invoice endpoint depends on group-billed meta and would
    // 404 for broken-out groups.
    if (groupBilled !== true) return;
    loadInvoice();
  }, [loadInvoice, groupBilled]);

  const reloadPayments = useCallback(async () => {
    if (!group?.id) return;
    try {
      const data = await getGroupPayments(group.id, token);
      setInvoice((prev) =>
        prev ? { ...prev, groupPayments: Array.isArray(data) ? data : [] } : prev
      );
    } catch (err) {
      console.error("Failed to reload group payments", err);
    }
  }, [group?.id, token]);

  // Adult bookings with charges
  const [adultBookings, setAdultBookings] = useState([]);

  const loadAdultBookings = useCallback(async () => {
    if (!adultIds.length || !token) { setAdultBookings([]); return; }
    try {
      const allBookings = [];
      for (const adultId of adultIds) {
        const res = await fetch(`${SERVER_URL}api/adults/${adultId}/adult-bookings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) continue;
        const data = await res.json();
        const bookings = Array.isArray(data) ? data : [];
        // Include bookings with a charge, or residential (type 0 / BOOKING) to show free nights
        for (const b of bookings) {
          const charge = Number(b.actualCharge || 0);
          const isResidential = b.participationType === "BOOKING";
          if (charge > 0 || isResidential) {
            allBookings.push(b);
          }
        }
      }
      setAdultBookings(allBookings);
    } catch (e) {
      console.error("Failed to load adult bookings for finance", e);
    }
  }, [adultIds, token]);

  useEffect(() => {
    if (groupBilled !== true) return;
    loadAdultBookings();
  }, [loadAdultBookings, groupBilled]);

  const members = useMemo(
    () => (Array.isArray(invoice?.members) ? invoice.members : []),
    [invoice]
  );
  const payments = useMemo(
    () => (Array.isArray(invoice?.groupPayments) ? invoice.groupPayments : []),
    [invoice]
  );

  // Build adult invoice lines using the same roll-up as student bookings
  const adultInvoiceLines = useMemo(() => {
    const mapped = adultBookings.map((b) => ({
      productName: `${b.productName || "Adult product"} (adult)`,
      startDate: b.customStart || "",
      endDate: b.customEnd || "",
      actualCharge: Number(b.actualCharge || 0),
      bookingId: b.id, id: b.id,
    }));
    if (!mapped.length) return [];
    const lines = buildInvoiceLines([{ bookings: mapped }]);
    lines.forEach((l) => { l.isAdultBooking = true; });
    return lines;
  }, [adultBookings]);

  const invoiceLines = useMemo(
    () => [...buildInvoiceLines(members), ...adultInvoiceLines].sort((a, b) => {
      const da = new Date(a.startDate || 0).getTime();
      const db = new Date(b.startDate || 0).getTime();
      if (da !== db) return da - db;
      return String(a.productName).localeCompare(String(b.productName));
    }),
    [members, adultInvoiceLines]
  );

  const totalGross = useMemo(
    () => invoiceLines.reduce((acc, l) => acc + l.grossTotal, 0),
    [invoiceLines]
  );
  const totalNet = useMemo(
    () => invoiceLines.reduce((acc, l) => acc + l.netTotal, 0),
    [invoiceLines]
  );

  const discountPayments = useMemo(
    () => payments.filter(isDiscountPayment),
    [payments]
  );
  const discountTotal = useMemo(
    () =>
      discountPayments.reduce(
        (acc, p) => acc + (Number(p.paymentAmount) || 0),
        0
      ),
    [discountPayments]
  );
  const paidExcludingDiscounts = useMemo(
    () =>
      payments
        .filter((p) => !isDiscountPayment(p))
        .reduce((acc, p) => acc + (Number(p.paymentAmount) || 0), 0),
    [payments]
  );
  const totalAlreadyPaid = paidExcludingDiscounts + discountTotal;

  const invoiceTotal = gross ? totalGross : totalNet;
  const balance = invoiceTotal - totalAlreadyPaid;

  const handleOpenCreate = () => {
    setEditingPayment(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (payment) => {
    setEditingPayment(payment);
    setDialogOpen(true);
  };

  const handleSavePayment = async (payload) => {
    setSaving(true);
    try {
      if (editingPayment?.id) {
        await updateGroupPayment(group.id, editingPayment.id, payload, token);
        showToast?.("Group payment updated", "success");
      } else {
        await createGroupPayment(group.id, payload, token);
        showToast?.("Group payment recorded", "success");
      }
      setDialogOpen(false);
      setEditingPayment(null);
      await reloadPayments();
    } catch (err) {
      console.error("Failed to save group payment", err);
      showToast?.(err?.message || "Failed to save group payment", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async (payment) => {
    const confirmed = window.confirm(
      `Delete group payment ${currency(payment.paymentAmount)} on ${payment.paymentDate}?`
    );
    if (!confirmed) return;
    try {
      await deleteGroupPayment(group.id, payment.id, token);
      showToast?.("Group payment deleted", "success");
      await reloadPayments();
    } catch (err) {
      console.error("Failed to delete group payment", err);
      showToast?.(err?.message || "Failed to delete group payment", "error");
    }
  };

  // ── PDF exclusion for zero-cost lines (checked by default = included) ──
  const [pdfExcluded, setPdfExcluded] = useState(new Set());
  const [invoiceHeaderNote, setInvoiceHeaderNote] = useState("");

  const lineKey = (line, idx) => `${line.productName}||${line.startDate}||${line.rate}||${idx}`;

  const togglePdfExclude = (key) => {
    setPdfExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ── Inline rate editing ──
  const [editingLineIdx, setEditingLineIdx] = useState(null);
  const [editingRate, setEditingRate] = useState("");
  const [savingRate, setSavingRate] = useState(false);

  const handleStartEditRate = (idx, currentRate) => {
    setEditingLineIdx(idx);
    setEditingRate(String(currentRate));
  };

  const handleCancelEditRate = () => {
    setEditingLineIdx(null);
    setEditingRate("");
  };

  const handleSaveRate = async (line) => {
    const newRate = Number(editingRate);
    if (isNaN(newRate) || newRate < 0) {
      showToast?.("Please enter a valid charge amount", "error");
      return;
    }
    if (!line.bookingIds?.length) {
      showToast?.("No bookings to update", "error");
      return;
    }

    setSavingRate(true);
    try {
      if (line.isAdultBooking) {
        // Update each adult booking individually
        for (const bid of line.bookingIds) {
          await fetchJson(`${SERVER_URL}api/adult-bookings/${bid}`, token, {
            method: "PUT",
            body: JSON.stringify({ actualCharge: newRate }),
          });
        }
        await loadAdultBookings();
      } else {
        const updates = line.bookingIds.map((id) => ({ id, actualCharge: newRate }));
        await fetchJson(`${SERVER_URL}api/bookings/batch-update`, token, {
          method: "POST",
          body: JSON.stringify({ updates }),
        });
        await loadInvoice();
      }
      showToast?.(`Updated ${line.bookingIds.length} booking(s) to ${currency(newRate)}`, "success");
      setEditingLineIdx(null);
      setEditingRate("");
    } catch (err) {
      console.error("Failed to update booking charges", err);
      showToast?.("Failed to update charges", "error");
    } finally {
      setSavingRate(false);
    }
  };

  // ── Delete invoice line (cancel bookings) ──
  const [deletingLine, setDeletingLine] = useState(null);

  const handleDeleteLine = async (line) => {
    if (!line.bookingIds?.length) {
      showToast?.("No bookings to cancel", "error");
      return;
    }
    const confirmed = window.confirm(
      `Cancel ${line.bookingIds.length} "${line.productName}" booking(s)? This will remove them from the invoice.`
    );
    if (!confirmed) return;

    setDeletingLine(line);
    try {
      if (line.isAdultBooking) {
        for (const bid of line.bookingIds) {
          await fetchJson(`${SERVER_URL}api/adult-bookings/${bid}`, token, {
            method: "DELETE",
          });
        }
        await loadAdultBookings();
      } else {
        await fetchJson(`${SERVER_URL}api/bookings/batch-delete`, token, {
          method: "POST",
          body: JSON.stringify({ ids: line.bookingIds }),
        });
        await loadInvoice();
      }
      showToast?.(`Deleted ${line.bookingIds.length} booking(s)`, "success");
    } catch (err) {
      console.error("Failed to cancel bookings", err);
      showToast?.("Failed to cancel bookings", "error");
    } finally {
      setDeletingLine(null);
    }
  };

  // ── Add Products (insurance / transfers) ──
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productDialogType, setProductDialogType] = useState(null); // "insurance" | "transfer"
  const [bespokeOpen, setBespokeOpen] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [addingProducts, setAddingProducts] = useState(false);

  const productTypeConfig = {
    insurance: {
      title: "Add insurance to all group members",
      filter: (p) => (p.productName || p.name || "").toLowerCase().includes("insurance"),
      icon: <ShieldIcon />,
      label: "insurance",
    },
    transfer: {
      title: "Add airport transfers to all group members",
      filter: (p) => {
        const name = (p.productName || p.name || "").toLowerCase();
        const type = p.productType;
        return name.includes("airport transfer") || type === 4 || type === "4";
      },
      icon: <FlightIcon />,
      label: "transfer",
    },
  };

  const handleOpenProductDialog = async (type) => {
    if (!studentIds.length) {
      showToast?.("No students in this group", "error");
      return;
    }
    const config = productTypeConfig[type];
    if (!config) return;

    setProductDialogType(type);
    setLoadingProducts(true);
    setProductDialogOpen(true);
    try {
      const staysRes = await fetchJson(
        `${SERVER_URL}api/students/stays/batch`,
        token,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ ids: [studentIds[0]] }),
        }
      );
      const stays = Array.isArray(staysRes) ? staysRes : [];
      const activeStay = stays.find((s) => s.arrivalDate && s.departureDate);

      if (!activeStay) {
        setAvailableProducts([]);
        setLoadingProducts(false);
        return;
      }

      const stayId = activeStay.stayId ?? activeStay.id;
      const eligible = await fetchJson(
        `${SERVER_URL}api/products/eligableProductsWide/stay/${stayId}`,
        token
      );
      const products = (eligible?.products || eligible || []);
      const filtered = products.filter(config.filter);

      setAvailableProducts(filtered);
      setSelectedProducts({});
    } catch (err) {
      console.error(`Failed to load ${type} products`, err);
      showToast?.(`Failed to load ${type} products`, "error");
      setAvailableProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProducts((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  const selectedProductCount = Object.values(selectedProducts).filter(Boolean).length;

  const handleAddProducts = async () => {
    const productIds = Object.entries(selectedProducts)
      .filter(([, v]) => v)
      .map(([id]) => Number(id));

    if (!productIds.length) return;

    setAddingProducts(true);
    try {
      const bookings = [];
      for (const sid of studentIds) {
        for (const pid of productIds) {
          bookings.push({ studentId: sid, productId: pid, actualCharge: null, bookingStatus: "PROVISIONAL" });
        }
      }
      const result = await fetchJson(`${SERVER_URL}api/bookings/batch-create`, token, {
        method: "POST",
        body: JSON.stringify({ bookings }),
      });
      const config = productTypeConfig[productDialogType];
      showToast?.(`Added ${result?.created ?? bookings.length} ${config?.label || ""} booking(s)`, "success");
      setProductDialogOpen(false);
      await loadInvoice();
    } catch (err) {
      console.error("Failed to add bookings", err);
      showToast?.("Failed to add bookings", "error");
    } finally {
      setAddingProducts(false);
    }
  };

  // Book a freshly-created bespoke EVENT product onto every group member at one
  // price per head (Phase 2). Rolls up to a single qty=N line on the group invoice.
  const bookBespokeForGroup = async ({ productId, perHead }) => {
    if (!studentIds.length) return;
    const bookings = studentIds.map((sid) => ({
      studentId: sid,
      productId,
      actualCharge: perHead,
      bookingStatus: "PROVISIONAL",
    }));
    await fetchJson(`${SERVER_URL}api/bookings/batch-create`, token, {
      method: "POST",
      body: JSON.stringify({ bookings }),
    });
    showToast?.(`Booked the event for ${studentIds.length} member(s)`, "success");
    await loadInvoice();
  };

  const handleDownloadPdf = () => {
    if (!invoice) return;
    // Filter out zero-cost lines that have been unchecked for PDF
    const pdfLines = invoiceLines.filter((line, idx) =>
      line.rate > 0 || !pdfExcluded.has(lineKey(line, idx))
    );
    generateGroupInvoicePDF({
      group: invoice.group || group,
      memberCount: members.length,
      invoiceLines: pdfLines,
      payments,
      totalGross,
      totalNet,
      discountPayments,
      discountTotal,
      paidExcludingDiscounts,
      totalAlreadyPaid,
      invoiceTotal,
      balance,
      gross,
      headerNote: invoiceHeaderNote,
    });
  };

  const handleBreakOut = async () => {
    const confirmed = window.confirm(
      `Break out "${group?.groupName || `Group ${group?.id}`}" for individual billing?\n\n` +
      `Students will be invoiced individually from their own profiles. ` +
      `Group booking meta (rate, dates, flights) is preserved — you can re-enable group ` +
      `billing later from this same screen.`
    );
    if (!confirmed) return;
    setBreakingOut(true);
    try {
      await breakOutToIndividual(group.id, token);
      showToast?.("Group broken out — students can now be invoiced individually", "success");
      setGroupBilled(false);
    } catch (err) {
      console.error("Break out failed", err);
      showToast?.(err?.message || "Break out failed", "error");
    } finally {
      setBreakingOut(false);
    }
  };

  const handleEnableGroupBilling = async () => {
    setEnablingBilling(true);
    try {
      await enableGroupBilling(group.id, token);
      showToast?.("Group billing re-enabled", "success");
      setGroupBilled(true);
      // loadInvoice will fire from its useEffect now that groupBilled flipped.
    } catch (err) {
      console.error("Enable group billing failed", err);
      showToast?.(err?.message || "Failed to re-enable group billing", "error");
    } finally {
      setEnablingBilling(false);
    }
  };

  if ((loadingInvoice && !invoice && groupBilled === true) || metaLoading) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (groupBilled === false) {
    const reEnableTooltip = hasIndividualPayments
      ? "Cannot re-enable — individual payments have been recorded against members of this group since it was broken out."
      : "Flip back to billing this group as a unit. The preserved booking meta will be reused.";
    return (
      <Box py={4} display="flex" justifyContent="center">
        <Alert
          severity="info"
          icon={<CallSplitIcon />}
          sx={{ maxWidth: 560 }}
          action={
            <Tooltip title={reEnableTooltip}>
              <span>
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleEnableGroupBilling}
                  disabled={enablingBilling || hasIndividualPayments}
                >
                  {enablingBilling ? "Enabling…" : "Re-enable group billing"}
                </Button>
              </span>
            </Tooltip>
          }
        >
          <AlertTitle>This group has been broken out for billing</AlertTitle>
          Students in this group can be invoiced individually from their own
          profiles (Payments and Invoice/Receipt tabs). Group invoice and
          group payments aren't available while this is set. The booking meta
          (default rate, dates, flight package) is preserved and will be
          restored if you re-enable group billing.
          {hasIndividualPayments && (
            <Box component="span" sx={{ display: "block", mt: 1, fontWeight: 600 }}>
              Individual payments have been recorded — re-enabling group
              billing is locked to avoid merging individual payment records
              back into a group-billed flow.
            </Box>
          )}
        </Alert>
      </Box>
    );
  }

  const hasGroupPayments = payments.length > 0;
  const breakOutTooltip = hasGroupPayments
    ? "Cannot break out — there are recorded group payments. Delete them first if you want to break out."
    : "Delete the group booking meta so students can be invoiced individually.";

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Typography variant="h6">
          Group finance — {group?.groupName || `Group ${group?.id}`}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ShieldIcon />}
            onClick={() => handleOpenProductDialog("insurance")}
            disabled={!invoice || !studentIds.length}
          >
            Add Insurance
          </Button>
          <Button
            variant="outlined"
            startIcon={<FlightIcon />}
            onClick={() => handleOpenProductDialog("transfer")}
            disabled={!invoice || !studentIds.length}
          >
            Add Transfers
          </Button>
          <Button
            variant="outlined"
            startIcon={<EventIcon />}
            onClick={() => setBespokeOpen(true)}
            disabled={!invoice || !studentIds.length}
          >
            Add bespoke event
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleDownloadPdf}
            disabled={!invoice}
          >
            Download PDF
          </Button>
          <Tooltip title={breakOutTooltip}>
            <span>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<CallSplitIcon />}
                onClick={handleBreakOut}
                disabled={breakingOut || hasGroupPayments}
              >
                {breakingOut ? "Breaking out…" : "Break out for individual billing"}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <TextField
        label="Invoice header note (optional)"
        placeholder="School / agent name and address, VAT number, etc."
        helperText="Free text shown in the header of the downloaded invoice PDF"
        value={invoiceHeaderNote}
        onChange={(e) => setInvoiceHeaderNote(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        size="small"
      />

      {/* ---------- Payments section ---------- */}
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1">Group payments</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Record payment
          </Button>
        </Stack>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((p) => {
                const discount = isDiscountPayment(p);
                const label = discount
                  ? p.paymentAccount
                  : `PAYMENT${p.notes ? `: ${p.notes}` : ""}`;
                return (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.paymentDate}</TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>{label}</TableCell>
                    <TableCell align="right">
                      {discount ? "−" : ""}
                      {currency(p.paymentAmount)}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(p)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeletePayment(p)}
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {payments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No payments recorded yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Divider />

      {/* ---------- Invoice preview ---------- */}
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Invoice preview
        </Typography>

        {invoiceLines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No bookings in this group yet.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" title="Include on PDF" />
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell align="right">Unit</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceLines.map((line, idx) => {
                  const lineTotal = gross ? line.grossTotal : line.netTotal;
                  const isEditing = editingLineIdx === idx;
                  return (
                    <TableRow key={`${line.productName}-${line.startDate}-${line.rate}-${idx}`}>
                      <TableCell padding="checkbox">
                        {line.rate === 0 ? (
                          <Checkbox
                            size="small"
                            checked={!pdfExcluded.has(lineKey(line, idx))}
                            onChange={() => togglePdfExclude(lineKey(line, idx))}
                            title="Include on PDF"
                          />
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{line.qty}</TableCell>
                      <TableCell>{line.productName}</TableCell>
                      <TableCell>
                        {line.startDate ? formatLocalDateOnly(new Date(line.startDate)) : ""}
                      </TableCell>
                      <TableCell>
                        {line.endDate ? formatLocalDateOnly(new Date(line.endDate)) : ""}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                            <TextField
                              size="small"
                              type="number"
                              value={editingRate}
                              onChange={(e) => setEditingRate(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveRate(line);
                                if (e.key === "Escape") handleCancelEditRate();
                              }}
                              autoFocus
                              disabled={savingRate}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0, step: "0.01" }}
                            />
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSaveRate(line)}
                              disabled={savingRate}
                              title="Save"
                            >
                              {savingRate ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                            </IconButton>
                          </Stack>
                        ) : (
                          <Box
                            onClick={() => handleStartEditRate(idx, line.rate)}
                            sx={{ cursor: "pointer", "&:hover": { color: "primary.main", textDecoration: "underline" } }}
                            title="Click to edit charge"
                          >
                            {currency(line.rate)}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">{currency(lineTotal)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteLine(line)}
                          disabled={deletingLine === line || !line.bookingIds?.length}
                          title="Cancel these bookings"
                        >
                          {deletingLine === line ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  Group total ({gross ? "Gross" : "Net"})
                </TableCell>
                <TableCell align="right">{currency(invoiceTotal)}</TableCell>
              </TableRow>

              {discountPayments.map((p) => {
                const reason = parseDiscountReason(p.paymentAccount) || "no reason";
                return (
                  <TableRow key={`discount-${p.id}`}>
                    <TableCell>Discount — {reason}</TableCell>
                    <TableCell align="right">
                      {`−${currency(Number(p.paymentAmount) || 0)}`}
                    </TableCell>
                  </TableRow>
                );
              })}

              <TableRow>
                <TableCell>Payments received</TableCell>
                <TableCell align="right">
                  {paidExcludingDiscounts > 0
                    ? `−${currency(paidExcludingDiscounts)}`
                    : currency(0)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Balance due</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {currency(balance)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Box>

      <GroupPaymentDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingPayment(null);
        }}
        onSave={handleSavePayment}
        initialData={editingPayment}
        saving={saving}
      />

      {/* ── Add Products dialog (insurance / transfers) ── */}
      <Dialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{productTypeConfig[productDialogType]?.title || "Add products"}</DialogTitle>
        <DialogContent>
          {loadingProducts ? (
            <Box py={3} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </Box>
          ) : availableProducts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No {productTypeConfig[productDialogType]?.label || ""} products available for this group's stay period.
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {availableProducts.map((p) => {
                const name = p.productName || p.name;
                const rate = p.defaultRate != null ? ` — £${Number(p.defaultRate).toFixed(2)}` : "";
                const dates = p.startDate && p.endDate
                  ? ` (${formatLocalDateOnly(new Date(p.startDate))} – ${formatLocalDateOnly(new Date(p.endDate))})`
                  : p.startDate
                  ? ` (${formatLocalDateOnly(new Date(p.startDate))})`
                  : "";
                return (
                  <FormControlLabel
                    key={p.id}
                    control={
                      <Checkbox
                        checked={!!selectedProducts[p.id]}
                        onChange={() => toggleProduct(p.id)}
                        disabled={addingProducts}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {name}{dates}{rate}
                      </Typography>
                    }
                  />
                );
              })}
              {selectedProductCount > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This will create {studentIds.length} x {selectedProductCount} = {studentIds.length * selectedProductCount} bookings
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialogOpen(false)} disabled={addingProducts}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddProducts}
            disabled={selectedProductCount === 0 || addingProducts}
            startIcon={addingProducts ? <CircularProgress size={16} /> : (productTypeConfig[productDialogType]?.icon || null)}
          >
            {addingProducts ? "Adding..." : `Add ${productTypeConfig[productDialogType]?.label || "products"}`}
          </Button>
        </DialogActions>
      </Dialog>

      <BespokeEventDialog
        open={bespokeOpen}
        onClose={() => setBespokeOpen(false)}
        defaultCampus={group?.campus || ""}
        onBook={bookBespokeForGroup}
      />
    </Stack>
  );
}

// ----------------------------------------------------------------------------
// PDF generation
// ----------------------------------------------------------------------------

function generateGroupInvoicePDF({
  group,
  memberCount,
  invoiceLines,
  payments,
  totalGross,
  totalNet,
  discountPayments = [],
  discountTotal,
  paidExcludingDiscounts,
  totalAlreadyPaid,
  invoiceTotal,
  balance,
  gross,
  headerNote = "",
}) {
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let y = 40;

  const brandNavy = [26, 31, 113];
  const brandPink = [230, 0, 126];
  const lightGrey = [245, 245, 245];

  const logo = process.env.PUBLIC_URL + "/assets/img/blissLogo.png";
  const footerImg = process.env.PUBLIC_URL + "/assets/img/footer.png";

  const tableDefaults = {
    theme: "grid",
    margin: { left: 40, right: 40 },
    styles: {
      fontSize: 10,
      cellPadding: 6,
      textColor: 40,
      lineColor: [180, 180, 180],
      lineWidth: 0.5,
    },
    headStyles: {
      fillColor: brandNavy,
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: lightGrey },
  };

  const today = formatLocalDateOnly(new Date());

  // Header
  const logoWidth = 50;
  const logoHeight = 60;
  pdf.addImage(logo, "PNG", 40, y, logoWidth, logoHeight);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.setTextColor(...brandNavy);
  pdf.text(
    paidExcludingDiscounts === 0 ? "Invoice" : "Receipt",
    pageWidth - 40,
    y + logoHeight / 2 + 8,
    { align: "right" }
  );

  const lineY = y + logoHeight + 10;
  pdf.setDrawColor(...brandNavy);
  pdf.setLineWidth(1);
  pdf.line(40, lineY, pageWidth - 40, lineY);

  y = lineY + 18;

  pdf.setFontSize(11);
  pdf.setTextColor(40);
  pdf.text(`Group: ${group?.groupName || ""}`, 40, y);
  pdf.text(`Date: ${today}`, pageWidth - 40, y, { align: "right" });
  y += 16;
  pdf.text(
    `Members: ${memberCount || 0}${group?.campus ? ` · Campus: ${group.campus}` : ""}`,
    40,
    y
  );

  y += 18;

  // Optional free-text header block (e.g. school/agent name & address, VAT number)
  const noteText = (headerNote || "").trim();
  if (noteText) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(40);
    const noteLines = pdf.splitTextToSize(noteText, pageWidth - 80);
    pdf.text(noteLines, 40, y);
    y += noteLines.length * 13 + 12;
  }

  // Rolled-up invoice lines (by product + dates + unit rate)
  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [["Qty", "Product", "Start", "End", "Unit", "Total"]],
    body:
      (invoiceLines || []).length > 0
        ? invoiceLines.map((line) => {
            const lineTotal = gross ? line.grossTotal : line.netTotal;
            return [
              String(line.qty),
              line.productName || "",
              line.startDate ? formatLocalDateOnly(new Date(line.startDate)) : "",
              line.endDate ? formatLocalDateOnly(new Date(line.endDate)) : "",
              `£ ${Number(line.rate || 0).toFixed(2)}`,
              `£ ${Number(lineTotal || 0).toFixed(2)}`,
            ];
          })
        : [["", "No bookings", "", "", "", ""]],
    columnStyles: {
      0: { halign: "right", cellWidth: 40 },
      1: { cellWidth: "auto" },
      2: { cellWidth: 70 },
      3: { cellWidth: 70 },
      4: { halign: "right", cellWidth: 70 },
      5: { halign: "right", cellWidth: 80 },
    },
  });

  y = pdf.lastAutoTable.finalY + 18;

  // Payments table
  if (y > pageHeight - 220) {
    pdf.addPage();
    y = 40;
  }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...brandNavy);
  pdf.text("Group Payments", pageWidth / 2, y, { align: "center" });
  y += 8;

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [["Date", "Notes", "Amount"]],
    body:
      payments.length > 0
        ? payments.map((p) => {
            const discount = isDiscountPayment(p);
            const label = discount
              ? p.paymentAccount || ""
              : `PAYMENT${p.notes ? `: ${p.notes}` : ""}`;
            return [
              p.paymentDate || "",
              label,
              `${discount ? "-£" : "£"} ${Number(p.paymentAmount || 0).toFixed(2)} GBP`,
            ];
          })
        : [["No payments recorded", "", ""]],
  });

  y = pdf.lastAutoTable.finalY + 18;

  // Totals
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...brandNavy);
  pdf.text("Totals", pageWidth / 2, y, { align: "center" });
  y += 8;

  const totalsBody = [
    [
      `Group total (${gross ? "Gross" : "Net"})`,
      `£ ${invoiceTotal.toFixed(2)} GBP`,
    ],
  ];

  discountPayments.forEach((p) => {
    const reason = parseDiscountReason(p.paymentAccount) || "no reason";
    const amt = Number(p.paymentAmount) || 0;
    totalsBody.push([
      `Discount — ${reason}`,
      `-£ ${amt.toFixed(2)} GBP`,
    ]);
  });

  totalsBody.push([
    "Payments received",
    paidExcludingDiscounts > 0
      ? `-£ ${paidExcludingDiscounts.toFixed(2)} GBP`
      : "£ 0.00 GBP",
  ]);
  totalsBody.push(["Balance due", `£ ${balance.toFixed(2)} GBP`]);

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [["Summary", "Amount"]],
    body: totalsBody,
    columnStyles: {
      0: { cellWidth: 340, fontStyle: "bold" },
      1: { cellWidth: "auto", halign: "right" },
    },
  });

  const finalPage = pdf.getNumberOfPages();
  pdf.setPage(finalPage);

  pdf.setDrawColor(...brandNavy);
  pdf.setLineWidth(1);
  pdf.line(40, pageHeight - 70, pageWidth - 40, pageHeight - 70);
  pdf.addImage(footerImg, "PNG", (pageWidth - 120) / 2, pageHeight - 55, 120, 35);

  const fileSlug = (group?.groupName || `group-${group?.id || ""}`)
    .replace(/[^a-z0-9]+/gi, "_")
    .toLowerCase();
  pdf.save(`${fileSlug}_${today}.pdf`);
}
